import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridRowSelectionModel,
  GridToolbarProps,
  ToolbarPropsOverrides,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { TFunction } from "i18next";
import React, { JSXElementConstructor } from "react";
import { useTranslation } from "react-i18next";
import { columnDef, GridTexts } from "./columnDef";
import ExpandOrCollapseCell from "./ExpandOrCollapseCell";
import NoRowsAvailableError from "./NoRowsAvailableError";
import SelectDropDownMenu from "./SelectDropDownMenu";
import { invoiceDataGridSx } from "./style";
import {
  CheckBoxSelectionProp,
  FilterType,
  InvoicesColumnVisibilityProps,
  InvoicesFilterState,
  InvoicesGridActionMenuOptions,
  InvoicesGridCallbacks,
  InvoicesGridContentVisibilityProps,
  InvoicesGridFilterProps,
  InvoicesRows,
  InvoicesToolbarProps,
  SelectDropDownMenuOptions,
  ToggleProps,
} from "./types";
import LoadingOverlay from "../../AllInvoices/components/LoadingOverlay";
import InvoicesGridToolBar, {
  InvoicesGridToolBarProps,
} from "../invoicesGridToolBar/InvoicesGridToolBar";

const InvoicesGrid = ({
  data,
  invoicesGridFilterProps,
  invoicesColumnVisibilityProps,
  dealerAccountId,
  invoicesGridContentVisibilityProps,
  checkBoxSelectionProp,
  selectDropDownMenuOptions,
  invoicesToolbarProps,
  toggleProps,
  isInvoicesLoading,
  error,
  invoicesFilterState,
  callbacks,
  invoicesGridActionsMenu,
  isDealerIdPersistenceEnabledForHU,
}: {
  data: InvoicesRows[];
  invoicesGridFilterProps: InvoicesGridFilterProps;
  invoicesColumnVisibilityProps: InvoicesColumnVisibilityProps;
  dealerAccountId: string;
  invoicesGridContentVisibilityProps: InvoicesGridContentVisibilityProps;
  checkBoxSelectionProp: CheckBoxSelectionProp;
  selectDropDownMenuOptions: SelectDropDownMenuOptions;
  invoicesToolbarProps: InvoicesToolbarProps;
  toggleProps: ToggleProps;
  isInvoicesLoading: boolean;
  error: boolean;
  invoicesFilterState: InvoicesFilterState;
  callbacks: InvoicesGridCallbacks;
  invoicesGridActionsMenu: InvoicesGridActionMenuOptions;
  isDealerIdPersistenceEnabledForHU: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["common", "invoices"]);
  const gridTexts = getGridTexts(t);

  const {
    setFilterValue,
    clearFilterValue,
    areInvoiceFilterQueryParametersSameAsFilter,
    filters,
    onSortModelChange,
  } = invoicesGridFilterProps;

  const {
    showNFENumber,
    showFacturaNumber,
    showOfficialDocumentNumber,
    showViewedDate,
    showViewedBy,
    showActions,
    showDealerAccount,
    enableInvoiceConsolidationForCheckBox,
  } = invoicesColumnVisibilityProps;

  const { showHULink, hasAccessToViewClaim, downloadingNFeNumber } =
    invoicesGridContentVisibilityProps;

  const {
    showDownloadButton,
    showConsolidateInvoiceButton,
    disableConsolidatedInvoiceDownloadButton,
    isConsolidateDownloadInProgress,
    isDownloadingAllInProgress,
    downloadInvoicesCallback,
    consolidateInvoicesCallback,
    showConsolidateInvoicesToExcelButton,
    disableConsolidateInvoicesToExcelButton,
    consolidateInvoicesToExcelCallback,
    onConsolidateInvoicesToExcelSuccess,
  } = invoicesToolbarProps;

  const {
    handleNFEClick,
    setUnauthorizedError,
    reloadCallback,
    selectionChanges,
  } = callbacks;

  const { handleCheckboxSelection, rowSelectionModel } = checkBoxSelectionProp;

  const { showViewedDetail } = toggleProps;

  const apiRef = useGridApiRef();

  const hiddenFields = [
    "__check__",
    "__tree_data_group__",
    "invoiceNumber",
    ...(showNFENumber ? [] : ["nfeNumber"]),
    ...(showFacturaNumber ? [] : ["factura"]),
    ...(showOfficialDocumentNumber ? [] : ["officialDocumentNumber"]),
    ...(showViewedDate ? [] : ["viewedDate"]),
    ...(showViewedBy ? [] : ["viewedBy"]),
    ...(showActions ? [] : ["actions"]),
    ...(showDealerAccount ? [] : ["dealerAccount"]),
  ];

  const getTogglableColumns = (gridColumns: GridColDef[]) =>
    gridColumns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);

  const invoicesGridToolBarProps: InvoicesGridToolBarProps = {
    disablePartNoSearch: invoicesGridFilterProps.disableFilters,
    partNoPlaceHolder: t("invoices:allInvoices.filters.PartNo.placeholder"),
    partNoValue: filters.partNumber || "",
    partNoOnChange: (value: string) =>
      setFilterValue(value, FilterType.PartNumber),
    partNoOnClear: () => clearFilterValue(FilterType.PartNumber),
    showDownloadButton,
    showConsolidateInvoiceButton,
    downloadInvoicesCallback,
    consolidateInvoicesCallback,
    setUnauthorizedError,
    disableConsolidatedInvoiceDownloadButton,
    isConsolidateDownloadInProgress,
    isDownloadingAllInProgress,
    showConsolidateInvoicesToExcelButton,
    disableConsolidateInvoicesToExcelButton,
    consolidateInvoicesToExcelCallback,
    onConsolidateInvoicesToExcelSuccess,
  };

  const columns = columnDef(
    theme,
    gridTexts,
    t,
    invoicesGridFilterProps,
    dealerAccountId,
    showHULink,
    hasAccessToViewClaim,
    handleNFEClick,
    invoicesGridActionsMenu,
    {
      onHeaderSelectChange: selectionChanges.onHeaderSelectChange,
      onRowSelectChange: selectionChanges.onRowSelectChange,
    },
    downloadingNFeNumber,
    isDealerIdPersistenceEnabledForHU,
    data
  );

  const groupingColDef: DataGridProProps["groupingColDef"] = {
    headerName: "",
    width: 48,
    renderCell: (params) => <ExpandOrCollapseCell {...params} />,
  };

  return (
    <Box
      data-testid="invoices-table"
      sx={{
        width: "100%",
        marginTop: theme.tokens.FuelSpacing4,
      }}
    >
      <DataGridPro
        apiRef={apiRef}
        checkboxSelection={enableInvoiceConsolidationForCheckBox}
        rows={data}
        columns={columns}
        columnBufferPx={columns.length + 1}
        loading={isInvoicesLoading}
        initialState={{
          columns: {
            columnVisibilityModel: {
              GRID_CHECKBOX_SELECTION_FIELD:
                enableInvoiceConsolidationForCheckBox,
              dealerAccount: showDealerAccount,
              nfeNumber: showNFENumber,
              factura: showFacturaNumber,
              officialDocumentNumber: showOfficialDocumentNumber,
              viewedDate: showViewedDate,
              viewedBy: showViewedBy,
              actions: showActions,
            },
          },
        }}
        treeData
        getTreeDataPath={(row) => row.group}
        groupingColDef={groupingColDef}
        getRowClassName={(params) =>
          `data-row-theme--${
            params.row.invoiceNumber === "" ? "Child" : "Parent"
          } ${
            showViewedDetail &&
            `data-row-theme--${params.row.isSeen ? "Seen" : "Unseen"}`
          }`
        }
        keepNonExistentRowsSelected
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(
          newRowSelectionModel: GridRowSelectionModel
        ) => {
          handleCheckboxSelection(newRowSelectionModel);
        }}
        localeText={{
          toolbarColumns: t("common:table.toolbarColumnsButton"),
          columnsManagementSearchTitle: t("common:table.search") || "Search",
          columnsManagementShowHideAllText:
            t("common:table.showHideAllColumn") || "Show/Hide",
          columnsManagementReset: t("common:table.reset") || "Reset",
        }}
        sx={invoiceDataGridSx(theme)}
        autoHeight
        headerFilters
        disableColumnResize
        disableRowSelectionOnClick
        hideFooter
        slots={{
          loadingOverlay: LoadingOverlay,
          columnResizeIcon: Divider,
          columnMenu: (props) => (
            <SelectDropDownMenu
              existingProps={props}
              {...selectDropDownMenuOptions}
            />
          ),
          columnMenuIcon: KeyboardArrowDownIcon,
          noRowsOverlay: () =>
            NoRowsAvailableError(
              error,
              t,
              reloadCallback,
              areInvoiceFilterQueryParametersSameAsFilter
            ),
          toolbar: InvoicesGridToolBar as JSXElementConstructor<
            GridToolbarProps & ToolbarPropsOverrides
          >,
        }}
        slotProps={{
          toolbar: invoicesGridToolBarProps,
          columnsManagement: { getTogglableColumns },
        }}
        sortingMode="server"
        onSortModelChange={onSortModelChange}
        sortModel={[
          {
            field: invoicesFilterState.sortField
              ? invoicesFilterState.sortField
              : "",
            sort: invoicesFilterState.sortDir === "asc" ? "asc" : "desc",
          },
        ]}
      />
    </Box>
  );
};

const getGridTexts = (t: TFunction<"translation", undefined>): GridTexts => ({
  invoiceNumber: t("invoices:allInvoices.header.invoiceNumber"),
  dealerAccount: t("invoices:allInvoices.header.dealerAccount"),
  nfeNumber: t("invoices:allInvoices.header.nfeNumber"),
  factura: t("invoices:allInvoices.header.facturaNumber"),
  officialDocumentNumber: t(
    "invoices:allInvoices.header.officialDocumentNumber"
  ),
  documentNumber: t("invoices:allInvoices.header.documentNumber"),
  handlingUnit: t("invoices:allInvoices.header.handlingUnit"),
  dealerReference: t("invoices:allInvoices.header.dealerReference"),
  createdDate: t("invoices:allInvoices.header.createdDate"),
  invoiceType: t("invoices:allInvoices.header.invoiceType"),
  orderType: t("invoices:allInvoices.header.orderType"),
  viewedDate: t("invoices:allInvoices.header.viewedDate"),
  viewedBy: t("invoices:allInvoices.header.viewedBy"),
  actions: t("invoices:allInvoices.dropDown.action"),
});

export default InvoicesGrid;
